import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n";
import { TutorialModeProvider } from "@/context/TutorialModeContext";
import { ToastProvider } from "@/components/ui/Toast";
import { ErrorBoundary, reloadOnceGuarded } from "@/components/ErrorBoundary";
import { App } from "@/App";
import { captureRefFromUrl } from "@/lib/affiliateRef";
import { captureFbclidFromUrl } from "@/lib/fbParams";
import { captureCampaignFromUrl } from "@/lib/campaignTouch";
import { captureShareFromUrl } from "@/lib/shareTouch";
import { isStorefrontHost, platformApex } from "@/lib/host";
import { prefetchStoreHomeIfLanding } from "@/lib/prefetch";
import { isChunkReloadSuppressed } from "@/lib/chunkLoader";
import "./index.css";

// Persist an affiliate ?ref= on first sight (last-click), before routing kicks in
// — the code usually lands on a sales page, not the checkout.
captureRefFromUrl();

// Same reasoning for Meta's ?fbclid= — the ad click lands on a sales page, so the
// click id must be persisted here or it's gone by the time an order is created.
captureFbclidFromUrl();
captureCampaignFromUrl();
// And the app a shared link came through (?sh=), for the same reason.
captureShareFromUrl();

// On a seller storefront ROOT the first thing rendered is StoreHome, a lazy
// chunk. Kick its fetch now — in parallel with React mounting — so it lands
// before the Suspense boundary asks for it, instead of one round-trip later.
// Deeper landings (…/checkout above all) render something else entirely, and
// there this fetch only competed with the chunks that page actually needed.
if (isStorefrontHost()) prefetchStoreHomeIfLanding();

// Vite fires this when a lazy route's module preload fails to load — a stale
// deploy (old index.html requesting a chunk hash that no longer exists) or, on
// iOS Safari, a first-load module fetch that aborts mid-navigation. Vite has
// already classified this as a preload failure, so recover unconditionally
// (guarded against loops) rather than pattern-matching the error message —
// WebKit's bare "Load failed" would otherwise not be recognised and the buyer
// lands on a blank checkout until they refresh by hand.
window.addEventListener("vite:preloadError", (event) => {
  // When loadChunk owns this import (any lazy route or prefetch), do NOT
  // preventDefault: Vite only rethrows the failure when the event is left
  // un-prevented, and loadChunk needs that rejection to run its retry / decide
  // whether to reload. Preventing it here would instead resolve the import to
  // `undefined`, which surfaces as a blank route with no recovery. loadChunk
  // itself triggers the one-shot reload only for a real navigation that fails
  // every retry; a background prefetch stays silent.
  if (isChunkReloadSuppressed()) return;
  // Any unwrapped dynamic import (outside loadChunk): keep the legacy net —
  // swallow the error and reload once so a stale-deploy chunk 404 self-recovers.
  event.preventDefault();
  reloadOnceGuarded(event.payload);
});

// Canonical host. Cloudflare keeps the production Pages alias
// (digital-embed-commerce.pages.dev) alive permanently, but the app lives on the
// configured apex. Bounce the bare alias over — preserving path/query/hash — so
// bookmarks and stale links land on the primary domain. Preview deploys
// (<hash>.digital-embed-commerce.pages.dev) are left alone. The destination reads
// from VITE_ROOT_DOMAIN rather than a literal so a domain move can't strand it.
if (window.location.hostname === "digital-embed-commerce.pages.dev") {
  const apex = platformApex();
  if (apex) {
    const dest = new URL(window.location.href);
    dest.hostname = apex;
    dest.protocol = "https:";
    window.location.replace(dest.toString());
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <LanguageProvider>
              <TutorialModeProvider>
                <App />
              </TutorialModeProvider>
            </LanguageProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);

// React has taken over #root — call off the index.html boot watchdog so it never
// reloads a page that actually loaded. (See the watchdog script in index.html.)
declare global {
  interface Window {
    __APP_MOUNTED__?: boolean;
    __clearBootWatchdog__?: () => void;
  }
}
window.__APP_MOUNTED__ = true;
window.__clearBootWatchdog__?.();
